<template>
    <div>
        <p>Use the live offer impact table to see the effect changing each price rule has on the applicable offers.</p>
        <!-- LIVE OFFERS IMPACT -->
        <div class="group-rounded-box">
            <span class="title">Live offer impact</span>

            <!-- FILTER TO SELECT CUSTOMER -->
            <div v-if="isCustomerMargin" class="row mx-0 pb-0-important">
                <div class="col-md-4">
                    <label class="mr-2 mb-0 mt-2">Display prices for:</label>
                    <multiselect 
                        v-model="selectedCustomer" 
                        :options="customerList" 
                        :allow-empty="false" 
                        :multiple="false" 
                        :close-on-select="false" 
                        :clear-on-select="false" 
                        :preserve-search="true" 
                        placeholder="Select a customer" 
                        track-by="id" 
                        selectedLabel="" 
                        selectLabel="" 
                        deselectLabel="" 
                        label="name" 
                    >
                        <template slot="selection" slot-scope="{ values, search, isOpen }">
                            <span class="multiselect__single" v-if="values.length && !isOpen">
                                <span v-if="values && values.length > 1">{{ values.length }} customer(s)</span>
                                <span v-else>{{ values[0].name }}</span>
                            </span>
                        </template>
                        <template slot="noResult">
                            No matching customer
                        </template>
                    </multiselect>
                </div>
            </div>

            <!-- FLAG TELLING USER THAT SETTLEMENT PRICES NEED TO BE UPDATED -->
            <div v-if="getPrices.length < 1" class="row mx-0 pb-0-important">
                <div 
                    class="col-md-12 px-0"
                    :class="{'no-results d-flex' : $breakpoint.mdAndDown}"
                >
                    <div class="w-100 alert alert-secondary mt-3 text-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        Offers will not be displayed to buyers until Settlement Prices have been updated
                    </div>
                </div>
            </div>

            <div v-else class="row">
                <div class="col-md-12 table-container table-with-scrolling">
                    <!-- NO OFFERS FOUND -->
                    <div v-if="$store.getters['sellerOffers/liveOffersSeller'].length < 1"
                        class="alert alert-secondary mt-3 text-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        You have not created any offers for this market
                    </div>
                    <!-- SELLER HAS NOT SELECTED ANY CUSTOMER FROM THE LIST -->
                    <div v-else-if="isCustomerMargin && !selectedCustomer"
                        class="alert alert-secondary mt-3 text-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        Please select a customer to view their prices
                    </div>
                    <!-- RULES ARE NOT APPLICABLE TO ANY OFFERS -->
                    <div v-else-if="offers.length < 1"
                        class="alert alert-secondary mt-3 text-center">
                        <i class="fas fa-info-circle mr-2"></i>
                        Rules above do not impact any of your offers
                    </div>
                    <!-- OFFERS TABLE -->
                    <table v-else class="table responsive-table">
                        <thead>
                            <tr>
                                <th>Fuel</th>
                                <th>Terminal</th>
                                <th>Bio Pricing</th>
                                <th>Pricing Method</th>
                                <th v-if="isCustomerMargin">Offer Price (ppl)</th>
                                <th v-else>Premium (ppl)</th>
                                <th v-if="isCustomerMargin">Customer Margin (ppl)</th>
                                <th v-else>Adjusted Premium (ppl)</th>
                                <th v-if="!isCustomerMargin">Override</th>
                                <th v-if="isCustomerMargin">Customer Price (ppl)</th>
                                <th v-else>Price (ppl)</th>
                                <!-- <th></th> -->
                            </tr>
                        </thead>
                        <tbody class="mh-350-px">
                            <tr v-for="(offer, idx) in offers" :key="idx" 
                                class="offer-rows" 
                                :class="{
                                    'disabled': getPrices.length < 1,
                                    'pt-2 pb-4': $breakpoint.mdAndDown
                                }"
                            >
                                <td label="Fuel" v-if=" offer.storage_type_id !=='BUNKERING' ">
                                    <span>{{ offer.fuel_id | fueltype }}</span>
                                </td>
                                <td label="Fuel" v-else>
                                    <span>Network</span>
                                </td>
                                <td label="Terminal" v-if="offer.storage_type_id !=='BUNKERING'">
                                    <span>{{ offer.terminal_id | terminalsname }} </span>
                                </td>
                                <td label="Network Type" v-else>                     
                                    <span>{{ networks.find(n => n._id === offer.networkId).name }}</span>
                                </td>
                                <td label="Bio Pricing">
                                    <span>{{ offer.fuel_id | offerbiopricing }}</span>
                                </td>
                                <td label="Pricing Method">
                                    <span>{{ offer.type_id | offerpricingmethod }}</span>
                                </td>
                                <td label="Offer Base Price (ppl)" v-if="isCustomerMargin">
                                    <span>{{ offer.basePrice | priceformat }}</span>
                                </td>
                                <td label="Original Premium (ppl)" v-else>
                                    <span v-if="offer.type_id === 'FIXED'">&mdash;</span>
                                    <span v-else>{{ offer.premium | priceformat }}</span>
                                </td>
                                <td label="Customer Margin (ppl)" v-if="isCustomerMargin">
                                    <span>{{ offer.adjustedPremium | priceformat }}</span>
                                </td>
                                <td label="Adjusted Premium (ppl)" v-else>
                                    <span v-if="offer.type_id === 'FIXED'">&mdash;</span>
                                    <span v-else>{{ offer.adjustedPremium | priceformat }}</span>
                                </td>
                                <td label="Override" v-if="!isCustomerMargin">
                                    <span v-if="offer.excludedRuleIds.some(id => rulesIds.includes(id))">
                                        <i 
                                            class="fas fa-question-circle fa-lg text-secondary" 
                                            data-toggle="tooltip" data-placement="right" 
                                            :title="getOverriddenRuleNames(offer.excludedRuleIds)"
                                        ></i>
                                    </span>
                                    <span v-else>&mdash;</span>
                                </td>
                                <td label="Price (ppl)">
                                    <span>{{ offer.price | priceformat }}</span>
                                    <i 
                                        v-if="offer.type_id === 'TRACKING'" 
                                        class="fas fa-sm fa-rss mb-2 ml-1" 
                                        :class="getPrices.length < 1 ? 'text-muted' : 'text-primary'" 
                                        data-toggle="tooltip" data-placement="right" 
                                        title="This Offer Price updates every 30 seconds"
                                    ></i>
                                </td>
                                <!-- <td class="td-actions">
                                    <button
                                        class="btn btn-icon text-secondary td-sticky td-left-bottom"
                                        :class="{'pt-3' : $breakpoint.mdAndDown}"
                                    >
                                        <i class="fas fa-edit fa-lg"></i>
                                    </button>
                                </td> -->
                            </tr>
                        </tbody>
                    </table>

                    <!-- SPINNER TO INDICATE THAT WE ARE LOADING THE DATA -->
                    <div v-if="isLoadingData" class="spinner-container">
                        <div class="spinner-border" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import breakpoint from '@/mixins/index';
import { mapGetters } from 'vuex';
import { getOffers } from "@/api/offers";
import { getOfferWithCostAdditions } from "@/util/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";
import { cloneDeep } from "lodash";

export default {
    mixins: [breakpoint],
    props: {
        rules: {
            type: Array,
            required: true
        },
        scope: {
            type: String,
            required: true
        }
    },
    data(){
        return {
            offers: [],
            overridingOfferRulesToDelete: [],
            selectedCustomer: null,
            isLoadingData: false
        }
    },
    updated(){
        $('[data-toggle="tooltip"]').tooltip();
    },
    computed: {
        ...mapGetters("accounts", ["customerList"]),
        ...mapGetters("livePrices", ["getPrices"]),
        ...mapGetters("metadata", ["productConfig", "networks"]),
        ...mapGetters("rules", ["globalRulesGetter"]),
        fetchOffers: function(){
            if (this.$store.getters["sellerOffers/liveOffersSeller"])
                this.getOffersToDisplay();
        },
        rulesIds: function(){
            return this.rules.map(rule => rule._id || rule); // Map existing rule IDs or just project the whole rule to match
        },
        isCustomerMargin: function(){
            return this.scope === costAdditionScope.Customer;
        }
    },
    watch: {
        rules: {
            handler(){
                this.getOffersToDisplay();
            },
            deep: true
        },
        selectedCustomer: {
            handler(customer){
                if (customer && customer.id) this.getOffersToDisplay();
            }
        },
        fetchOffers(){}
    },
    methods: {
        getOffersToDisplay: async function(){
            this.isLoadingData = true;
            let offersList = [];

            if (this.getPrices.length > 0){
                if (this.isCustomerMargin){ // If this is customer margin then fetch offers as the selected customer
                    try {
                        const params = { 
                            isSellerImpersonatingBuyer: true,
                            accountId: this.selectedCustomer.id,
                            type: 'buyer',
                            includeAuditTrail: true
                        };
                        const { offers } = await getOffers(params);
                        offersList = offers;
                    } catch(error) {
                        offersList = [];
                    }
                } else { // Otherwise use the list of all of the seller's offers
                    offersList = this.$store.getters["sellerOffers/liveOffersSeller"];
                }
            }

            await this.getUpdatedOffers(offersList);
            this.isLoadingData = false;
        },
        getUpdatedOffers: function(offersList){
            offersList = JSON.parse(JSON.stringify(offersList)); // Deep copy the object to avoid binding

            // Get the price variables for calculation
            const fxSettlementRule = this.globalRulesGetter && this.globalRulesGetter.dailySettlementRules && this.globalRulesGetter.dailySettlementRules.find(rule => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.Fx);
            const fxSettle = fxSettlementRule ? Number(fxSettlementRule.action.value) : this.$store.getters["livePrices/marketData"].fx.settle;
            const iceLive = this.$store.getters["livePrices/marketData"].ice.live;
            const staMatrix = this.$store.getters["metadata/staMatrix"];

            // Calculate the premium and final price
            for (let offer of offersList){
                const product = this.getPrices.find(product => product.fuel_id === offer.fuel_id);
                const costAdditionRules = offer.pricingAuditTrail.filter(rule => rule.type === rulesEnum.eType.Cumulative);
                const metadata = {
                    product, 
                    fxSettle, 
                    iceLive, 
                    productIds: this.productConfig, 
                    staMatrix,
                    eOfferExclusion: rulesEnum.eOfferExclusion
                };
                let rules = cloneDeep(this.rules);
                if (rules && rules.length > 0) {
                    rules.forEach(rule => {
                        rule.action.value = Number((rule.action.value ?? 0).toString().replace(/,/g, '')).toFixed(2);
                    });
                }
                offer = getOfferWithCostAdditions(offer, costAdditionRules, rules, metadata, this.selectedCustomer && this.selectedCustomer.id);
                
                // Add overriding offer-specific rules to delete that exists in the list of rules within this component
                const newOverridingOfferRulesToDelete = offer.overridingOfferRulesToDelete.filter(rule => this.rulesIds.includes(rule.ruleToOverride) && !this.overridingOfferRulesToDelete.find(r => r._id === rule._id));
                if (newOverridingOfferRulesToDelete.length > 0) this.overridingOfferRulesToDelete.push(...newOverridingOfferRulesToDelete);
            }
            
            this.offers = offersList.filter(offer => {
                if (!this.isCustomerMargin){
                    // Let's filter the list so we are only showing offers that got impacted by the rules
                    return offer.pricingAuditTrail.some(rule => this.rulesIds.includes(rule._id || rule)) || offer.excludedRuleIds.some(id => this.rulesIds.includes(id));
                } else {
                    // Return all offers except contracts
                    return !offer.isContract;
                }
            });
            this.$emit("setOverridingOfferRulesToDelete", this.overridingOfferRulesToDelete); // Assign a list of overriding offer-specific rules to delete to the parent component
        },
        getOverriddenRuleNames: function(excludedRuleIds){
            let ruleNamesToDisplay = "";

            const overriddenRules = this.rules.filter(rule => excludedRuleIds.includes(rule._id));
            for (const [idx, rule] of overriddenRules.entries()){
                const ruleName = rule.name;
                if (idx == 0)
                    ruleNamesToDisplay = rule.name;
                else if (idx == overriddenRules.length - 1)
                    ruleNamesToDisplay += ` and ${ruleName}`;
                else
                    ruleNamesToDisplay += `, ${ruleName}`;
            }

            ruleNamesToDisplay += overriddenRules.length > 1 ? " are" : " is";

            return `${ruleNamesToDisplay} overridden at offer level`;
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/_spinner.scss";
.group-rounded-box .row.pb-0-important {
    padding-bottom: 0!important;
}
</style>